'use client'

import {useTranslation} from '../../../hooks/useTranslation'
import {cn} from '../../../lib/utils'
import type {PublicPartnerServiceTerms} from '../../../services/service-terms.service'
import {PUBLIC_SITE_CONTENT_CLASS_NAME, PublicSiteShell} from './PublicSiteShell'
import {publicEntryStyles, publicTermsStyles} from './publicEntryStyles'

type PublicTermsPageProps = {
    partnerName: string
    showAboutPage?: boolean
    terms: PublicPartnerServiceTerms
}

type TermsHeading = {
    number: string
    title: string
}

const SHOW_TERMS_TABLE_OF_CONTENTS = false

export function PublicTermsPage({partnerName, showAboutPage = true, terms}: PublicTermsPageProps) {
    const {t} = useTranslation('landing')
    const hasTerms = terms.available && terms.html.trim().length > 0
    const tableOfContents = SHOW_TERMS_TABLE_OF_CONTENTS ? extractTableOfContents(terms.html) : []
    const hasTableOfContents = tableOfContents.length > 0

    return (
        <PublicSiteShell
            activePage="terms"
            partnerName={partnerName}
            showAboutPage={showAboutPage}
        >
            <div className={cn(PUBLIC_SITE_CONTENT_CLASS_NAME, publicEntryStyles.pagePaddingLoose)}>
                <section aria-labelledby="public-terms-content">
                    <h1 id="public-terms-content" className="sr-only">{t('publicEntry.terms.title')}</h1>
                    {hasTerms ? (
                        <div className={hasTableOfContents ? publicTermsStyles.contentWithToc : publicTermsStyles.contentShell}>
                            {hasTableOfContents ? (
                                <aside className={publicTermsStyles.tocAside}>
                                    <p className={publicTermsStyles.tocTitle}>{t('publicEntry.terms.toc')}</p>
                                    <ol className={publicTermsStyles.tocList}>
                                        {tableOfContents.map((item) => (
                                            <li key={`${item.number}-${item.title}`} className={item.number === '01' ? publicTermsStyles.tocActiveItem : ''}>
                                                <span className="mr-5">{item.number}</span>
                                                {item.title}
                                            </li>
                                        ))}
                                    </ol>
                                </aside>
                            ) : null}
                            <article
                                data-testid="public-terms-html"
                                className={publicTermsStyles.termsHtml}
                                dangerouslySetInnerHTML={{__html: terms.html}}
                            />
                        </div>
                    ) : (
                        <div className={publicEntryStyles.panel}>
                            {t('publicEntry.terms.empty')}
                        </div>
                    )}
                </section>
            </div>
        </PublicSiteShell>
    )
}

function extractTableOfContents(html: string): TermsHeading[] {
    const headings = Array.from(html.matchAll(/<h([2-3])\b[^>]*>([\s\S]*?)<\/h\1>/gi))

    return headings
        .map((match, index) => ({
            number: String(index + 1).padStart(2, '0'),
            title: decodeHtmlText(stripHtml(match[2] ?? '')),
        }))
        .filter((heading) => heading.title.length > 0)
}

function stripHtml(value: string): string {
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

function decodeHtmlText(value: string): string {
    return value
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
}
